import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";
import {
  InvestigationAdvertisement,
  InvestigationSectionHeader,
  Separator,
} from "../components";
import { patchInvestigation } from "../../../../../apis";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { useConstants } from "../Main/useConstants";
import { AddConclusion } from "./AddConclusion";
import { SingleConclusion } from "./SingleConclusion";
import { Conclude } from "./Modals/Conclude";
import _ from "lodash";

export const Conclusion = ({
  investigation,
  setInvestigation,
  justView = false,
  handleSelectedSection,
  onReload,
  onReloadReport,
}) => {
  const [t] = useTranslation("investigation");
  const { enums } = useConstants();

  const [conclusion, setConclusion] = useState(() => {
    if (investigation.conclusion === null) return null;
    return {
      conclusion: investigation.conclusion,
      conclusion_at: investigation.conclusion_at,
      comment: investigation.comment ?? "",
      files: investigation.conclusion_files ?? [],
      measures: investigation.measures ?? [],
      main_evidence: investigation.main_evidence ?? "",
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showConclude, setShowConclude] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const someConclusionExist = investigation.conclusion !== null;

  useEffect(() => {
    if (investigation.conclusion !== null) {
      setConclusion({
        conclusion: investigation.conclusion,
        conclusion_at: investigation.conclusion_at,
        comment: investigation.comment ?? "",
        files: investigation.conclusion_files ?? [],
        measures: investigation.measures ?? [],
        main_evidence: investigation.main_evidence ?? "",
      });
    }
  }, [investigation]);

  const handleSaveConclusionDetails = async (newConclusions) => {
    setIsLoading(true);
    const objToSave = _.cloneDeep(newConclusions);
    try {
      await patchInvestigation(investigation.alias, objToSave);
      setInvestigation((prev) => ({
        ...prev,
        conclusion: objToSave.conclusion,
        comment: objToSave.comment,
        conclusion_at: objToSave.conclusion_at,
        main_evidence: objToSave.folder?.main_evidence || null,
        folder: {
          ...prev.folder,
          ...objToSave.folder,
        }
      }));
      onReload();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🎯 Funciones auxiliares para renderizar
  const renderHeader = () =>
    !isEditing &&
    !justView && (
      <InvestigationSectionHeader
        text={t("Conclusion.add_conclusion_to_investigation")}
        buttonText={t("Conclusion.add_conclusion_to_investigation_btn")}
        buttonIcon="outline_clipboard_task"
        hideButton={someConclusionExist}
        onClick={() => setIsEditing(true)}
      />
    );

  const renderAdvertisement = () =>
    !someConclusionExist &&
    !isEditing &&
    !justView && (
      <InvestigationAdvertisement
        title={t("Conclusion.no_conclusions_loaded")}
        description={t(
          "Conclusion.no_conclusions_loaded_description"
        )}
      />
    );

  const renderSectionTitle = () =>
    someConclusionExist &&
    justView && (
      <Separator
        text={t("Conclusion.conclusions")}
        icon="outline_clipboard_task"
        iconButton="outline_edit"
        onClick={() => handleSelectedSection(enums.sections.conclusion)}
        sx={{ marginBottom: 1.5 }}
      />
    );

  const renderConclusion = () =>
    someConclusionExist &&
    !isEditing && (
      <Stack padding={justView ? 0 : 2} spacing={2}>
        {conclusion && (
          <SingleConclusion
            conclusion={conclusion}
            setIsEditing={setIsEditing}
            handleSaveConclusionDetails={handleSaveConclusionDetails}
            justView={justView}
            assignsDetails={investigation?.assigns_details || []}
          />
        )}
        {investigation.conclusion_at == null && !justView && (
          <Box display="flex" justifyContent="flex-end">
            <EGButton
              label={t("Conclusion.conclude_investigation_btn")}
              variant="contained"
              color="error"
              onClick={() => setShowConclude(true)}
              sx={{ width: "fit-content" }}
            />
          </Box>
        )}
      </Stack>
    );

  return (
    <>
      {someConclusionExist && !justView && (
        <Conclude
          show={showConclude}
          onHide={() => setShowConclude(false)}
          handleSaveConclusionDetails={handleSaveConclusionDetails}
          conclusion={conclusion}
          assignsDetails={investigation?.assigns_details || []}
          onReloadReport={onReloadReport}
        />
      )}
      <Box className="dyTheme1">
        {renderHeader()}
        {renderAdvertisement()}
        {renderSectionTitle()}
        {renderConclusion()}
        {isEditing && (
          <AddConclusion
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            conclusion={conclusion}
            setIsEditing={setIsEditing}
            handleSaveConclusionDetails={handleSaveConclusionDetails}
            assignsDetails={investigation?.assigns_details || []}
            onReloadReport={onReloadReport}
          />
        )}
      </Box>
    </>
  );
};
